import { t } from 'i18next';
import { RouteObject } from 'react-router-dom';
import { BaseOutlet } from '../../../components/BaseOutlet/BaseOutlet';
import {
  _PeopleDocument,
  _PeopleQuery,
  _PeopleQueryVariables,
  ClassesListDocument,
  ClassesListQuery,
  ClassesListQueryVariables,
  SubjectsListDocument,
  SubjectsListQuery,
  SubjectsListQueryVariables,
  TimetableBlocksDocument,
  TimetableBlocksQuery,
  TimetableBlocksQueryVariables,
} from '../../../types/planung-graphql-client-defs';
import { urqlClient } from '../../../utils/urqlClient';
import { DeputateEvaluateCurrentBySubjectSubpage } from './Annual/Current/DeputateEvaluateCurrentBySubjectSubpage';
import { DeputateEvaluateCurrentOverviewSubpage } from './Annual/Current/DeputateEvaluateCurrentOverviewSubpage';
import { DeputateEvaluateCurrentPage } from './Annual/Current/DeputateEvaluateCurrentPage';
import { DeputateEvaluateCurrentReportsSubpage } from './Annual/Current/DeputateEvaluateCurrentReportsSubpage';
import { DeputateAnnualPage } from './Annual/DeputateAnnualPage';
import { DeputateEvaluatePage } from './DeputateEvaluatePage';
import { DeputateEvaluatePlannedClassDetailSubpage } from './Planned/DeputateEvaluatePlannedClassDetailSubpage';
import { DeputateEvaluatePlannedClassesSubpage } from './Planned/DeputateEvaluatePlannedClassesSubpage';
import { DeputateEvaluatePlannedOverviewSubpage } from './Planned/DeputateEvaluatePlannedOverviewSubpage';
import { DeputateEvaluatePlannedPage } from './Planned/DeputateEvaluatePlannedPage';
import { DeputateEvaluatePlannedReportsSubpage } from './Planned/DeputateEvaluatePlannedReportsSubpage';
import { DeputateEvaluatePlannedSubjectDetailSubpage } from './Planned/DeputateEvaluatePlannedSubjectDetailSubpage';
import { DeputateEvaluatePlannedSubjectsSubpage } from './Planned/DeputateEvaluatePlannedSubjectsSubpage';
import { DeputateEvaluatePlannedTeacherDetailSubpage } from './Planned/DeputateEvaluatePlannedTeacherDetailSubpage';

export const deputateEvaluateRoutes = (baseSlug: string): RouteObject[] => {
  return [
    {
      index: true,
      element: <DeputateEvaluatePage />,
    },

    // Plan Deputate
    {
      path: t('routes.deputate.evaluate.planned.slug'),
      element: <DeputateEvaluatePlannedPage baseSlug={baseSlug} />,
      handle: {
        crumb: () => ({
          slug: `${baseSlug}/${t('routes.deputate.evaluate.planned.slug')}`,
          title: t('routes.deputate.evaluate.planned.title'),
        }),
      },
      children: [
        {
          index: true,
          element: <DeputateEvaluatePlannedOverviewSubpage />,
        },

        {
          path: ':teacherUuid',
          loader: ({ params }) => {
            return urqlClient
              .query<_PeopleQuery, _PeopleQueryVariables>(_PeopleDocument, { where: { uuid: params.teacherUuid } })
              .toPromise();
          },
          handle: {
            crumb: ({ data }: { data?: _PeopleQuery }) => {
              return [
                {
                  slug: `${baseSlug}/${t('routes.deputate.evaluate.planned.slug')}/${data?.people[0].uuid}`,
                  title: data?.people[0].fullName,
                },
              ];
            },
          },
          element: <DeputateEvaluatePlannedTeacherDetailSubpage />,
        },

        {
          path: t('routes.deputate.byClass.slug'),
          element: <BaseOutlet />,
          handle: {
            crumb: () => ({
              slug: `${baseSlug}/${t('routes.deputate.evaluate.planned.slug')}/${t('routes.deputate.byClass.slug')}`,
              title: t('routes.deputate.byClass.title'),
            }),
          },
          children: [
            {
              element: <DeputateEvaluatePlannedClassesSubpage />,
              index: true,
            },
            {
              path: ':classUuid',
              element: <DeputateEvaluatePlannedClassDetailSubpage />,
              loader: ({ params }) => {
                return urqlClient
                  .query<ClassesListQuery, ClassesListQueryVariables>(ClassesListDocument, {
                    classUuid: params.classUuid,
                  })
                  .toPromise();
              },
              handle: {
                crumb: ({ data }: { data: ClassesListQuery }) => {
                  return [
                    {
                      slug: `${baseSlug}/${t('routes.deputate.evaluate.planned.slug')}/${t('routes.deputate.byClass.slug')}/${
                        data.classes[0].uuid
                      }`,
                      title: data.classes[0].name,
                    },
                  ];
                },
              },
            },
          ],
        },

        {
          path: t('routes.deputate.evaluate.bySubject.slug'),
          element: <BaseOutlet />,
          handle: {
            crumb: () => ({
              slug: `${baseSlug}/${t('routes.deputate.evaluate.planned.slug')}/${t('routes.deputate.evaluate.bySubject.slug')}`,
              title: t('routes.deputate.evaluate.bySubject.title'),
            }),
          },
          children: [
            {
              element: <DeputateEvaluatePlannedSubjectsSubpage />,
              index: true,
            },
            {
              path: ':subjectUuid',
              element: <DeputateEvaluatePlannedSubjectDetailSubpage />,
            },
          ],
        },

        {
          path: t('routes.deputate.evaluate.reports.slug'),
          element: <DeputateEvaluatePlannedReportsSubpage />,
          handle: {
            crumb: () => ({
              slug: `${baseSlug}/${t('routes.deputate.evaluate.reports.slug')}`,
              title: t('routes.deputate.evaluate.reports.title'),
            }),
          },
        },
      ],
    },

    // Jahresdeputate
    {
      path: t('routes.deputate.evaluate.annual.slug'),
      element: <BaseOutlet />,
      handle: {
        crumb: () => ({
          slug: `${baseSlug}/${t('routes.deputate.evaluate.annual.slug')}`,
          title: t('routes.deputate.evaluate.annual.title'),
        }),
      },

      children: [
        {
          element: <DeputateAnnualPage />,
          index: true,
        },

        // IST-Deputate
        {
          path: ':blockUuid',
          element: <DeputateEvaluateCurrentPage />,
          loader: ({ params }) => {
            return urqlClient
              .query<TimetableBlocksQuery, TimetableBlocksQueryVariables>(TimetableBlocksDocument, {
                where: { uuid: params.blockUuid },
              })
              .toPromise();
          },
          handle: {
            crumb: ({ data }: { data?: TimetableBlocksQuery }) => ({
              slug:
                data && data.timetableBlocks.length > 0
                  ? `${baseSlug}/${t('routes.deputate.evaluate.annual.slug')}/${data?.timetableBlocks.map((tb) => tb.uuid).join(', ')}`
                  : `${baseSlug}/${t('routes.deputate.evaluate.annual.slug')}/${t('routes.deputate.evaluate.all.slug')}`,
              title:
                data && data.timetableBlocks.length > 0
                  ? data.timetableBlocks.map((tb) => tb.timetables.map((tt) => tt.name).join(', ')).join(', ')
                  : t('routes.deputate.evaluate.all.title'),
            }),
          },
          children: [
            {
              element: <DeputateEvaluateCurrentOverviewSubpage />,
              index: true,
            },
            {
              path: t('routes.deputate.evaluate.bySubject.slug'),
              element: <DeputateEvaluateCurrentBySubjectSubpage />,
              loader: ({ params }) => {
                return urqlClient
                  .query<TimetableBlocksQuery, TimetableBlocksQueryVariables>(TimetableBlocksDocument, {
                    where: { uuid: params.blockUuid },
                  })
                  .toPromise();
              },
              handle: {
                crumb: ({ data }: { data?: TimetableBlocksQuery }) => ({
                  slug: `${baseSlug}/${t('routes.deputate.evaluate.annual.slug')}/${data?.timetableBlocks.map((tb) => tb.uuid).join(', ')}/${t('routes.deputate.evaluate.bySubject.slug')}`,
                  title: t('routes.deputate.evaluate.bySubject.title'),
                }),
              },
            },
            {
              path: t('routes.deputate.evaluate.reports.slug'),
              element: <DeputateEvaluateCurrentReportsSubpage />,
              loader: ({ params }) => {
                return urqlClient
                  .query<TimetableBlocksQuery, TimetableBlocksQueryVariables>(TimetableBlocksDocument, {
                    where: { uuid: params.blockUuid },
                  })
                  .toPromise();
              },
              handle: {
                crumb: ({ data }: { data?: TimetableBlocksQuery }) => ({
                  slug: `${baseSlug}/${t('routes.deputate.evaluate.annual.slug')}/${data?.timetableBlocks.map((tb) => tb.uuid).join(', ')}/${t('routes.deputate.evaluate.reports.slug')}`,
                  title: t('routes.deputate.evaluate.reports.title'),
                }),
              },
            },
            {
              path: `${t('routes.lessons.plan.teachers.slug')}/:teacherUuid`,
              loader: ({ params }) => {
                return urqlClient
                  .query<_PeopleQuery, _PeopleQueryVariables>(_PeopleDocument, { where: { uuid: params.teacherUuid } })
                  .toPromise();
              },
              handle: {
                crumb: ({ data }: { data: _PeopleQuery }) => {
                  return [
                    {
                      slug: `${baseSlug}/${t('routes.lessons.slug')}/${t('routes.lessons.plan.teachers.slug')}`,
                      title: `${t('persons.title', { context: 'neutral', count: 1 })}: ${data?.people[0].fullName}`,
                    },
                  ];
                },
              },
              async lazy() {
                const { DeputateEvaluateCurrentTeachersDetailSubpage } = await import(
                  './Annual/Current/DeputateEvaluateCurrentTeachersDetailSubpage'
                );
                return { element: <DeputateEvaluateCurrentTeachersDetailSubpage /> };
              },
            },
          ],
        },
      ],
    },
  ];
};
